import React, { useEffect, useRef } from "react";

/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { GameClient } from "../../gameClient";
import { UIButton, UIButtonSmall } from "../../styles/ButtonStyle";
import { Sound } from "../../../../shared/sound";
import { clearUnannouncedAchievements, getDismissedEAPopup, getDontShowTutorial, getLadderRank, getMainVolume, getMusicVolume, getSfxVolume, getTrainingLevel, getUserSettings, setTrainingLevel } from "../../userSettings";
import { TutorialId, allTutorials } from "../TooltipInfo";
import { TrainingLevel } from "../../../../shared/playerAccount";
import { LeagueMarker, RemainingMarker, TierMarker } from "../TierMarker";
import { Colors } from "../../palettes/colorPalette";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCopy } from "@fortawesome/free-solid-svg-icons";
import { StartGameTree, GameTreeSelection } from "../StartGameTree";
import { GameMode } from "../../../../shared/game";
import { EAPopup } from "../EAPopup";
import { Multiplayer, UIConstants } from "../../../../shared/constants";
import { backgroundTexture } from "../../uiConstants";
import { DesktopChromeHostNotice, DesktopChromeJoinerNotice, isDesktopChrome } from "../PrivacyWarning";
import { getWebServicesList } from "../../networking/webServicesList";
import { AchievementChecklist, ChecklistIcon } from "../AchievementChecklist";
import { Debug } from "../../../../shared/debug";
import { clearPendingSteamLobbyId, getPendingSteamLobbyId } from "../../networking/pendingSteamInvite";
import { getQueryParams } from "../../../../shared/utils";
import { clearConnectionStatus, setConnectionError, setConnectionStep } from "../MultiplayerConnectionStatus";
import { ConnectionToServer, WebWorkerClient, WSClient } from "../../networking/connectionToServer";
import { ConnectionToRemotePeer, SteamConnectionToRemotePeer, WebRTCConnectionToRemotePlayer } from "../../networking/connectionToRemotePeer";
import { isSteamNetworkingAvailable } from "../../networking/steamNetworking";
import { SteamConnectionPoller } from "../../networking/steamPeerConnection";
import { joinSteamLobby } from "../../networking/steamLobby";
import { WebRTCConnectionPoller } from "../../networking/webrtcPeerConnection";
import { readContinuableMultiplayerSave, readContinuableSoloSave } from "../../../../shared/savedGameUtils";

declare var gameClient: GameClient;

const containerStyle = css`
    user-select:none;
    overflow: hidden;
    max-height: 100vh;
    margin: auto;
    position: absolute;
    top:0;
    bottom:0;
    left:0;
    right:0;
    color: white;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-evenly;
`;

const menuStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width 20vw;
    height: 100%;
`;

const buttonStyle = css`
    margin-bottom: 2.5vh;
`;

const logoStyle = css`
    width: 80vh;
    height: 50vh;
`;

const noticePanelStyle = css`
    position: absolute;
    left: 50%;
    z-index: 20;
    background: url(${backgroundTexture});
    background-color: ${Colors.UIBackground};
    padding: 0.75vw 1.5vw;
    filter: drop-shadow(2px 4px 2px ${Colors.UITextAndBorders});
    font-size: 1.5vw;
    color: ${Colors.UITextAndBorders};
    text-align: center;
    max-width: 40vw;
`;

//Dead centre, over the logo: copying this link is the only thing the host is on this screen to do
const hostLinkPanelStyle = css`
    top: 50%;
    transform: translate(-50%, -50%);
    cursor: pointer;
`;

//Below the logo, clear of the tooltip that pops up over the top of the screen
const joinerNoticePanelStyle = css`
    top: 78%;
    transform: translate(-50%, 0);
`;

interface LobbyProps {
    startGame: (mode: GameMode, continueGame?: string | null) => void;
    showCredits: () => void;
    showSettings: (show: boolean) => void;
    showTutorial: (tutorials: TutorialId[]) => void;
    setCurtainOpacity: (opacity: number) => void;
    setCurtainFadeSpeed: (speed: number) => void;
    isSpawnGame: boolean;
    peerConnected: boolean;
    hostId?: string;
}

const onButtonExit = () => {
    document.getElementById('lobby_tooltip')!.innerHTML = '';
    document.getElementById('lobby_tooltip')!.style.display = 'none';
}

type MenuLevels = "start" | "play-options" | "multiplayer-options";

export const LobbyScreen = (props: LobbyProps) => {
    const {
        startGame,
        peerConnected
    } = props;

    console.log("Rendering LobbyScreen " + performance.now());

    const getPlayText = () => {
        //TODO: This is only a temporary training level display.
        let playText;
        playText = "Play";
        return playText;
    }

    //const inProgressHostingConnection = useRef<ConnectionToRemotePeer | null>(null);
    const inProgressJoiningConnection = useRef<SteamConnectionPoller | WebRTCConnectionPoller | null>(null);

    //Set when the player chooses to resume a multiplayer game: the invite flow runs as normal and
    //Start Game loads this save instead of dealing a fresh one.
    const resumeSaveName = useRef<string | null>(null);

    const [connected, setConnected] = React.useState(process.env.is_standalone ? true : false);
    const [buttonText, setButtonText] = React.useState(process.env.is_standalone ? getPlayText() : "Join Lobby");
    const [position, setPosition] = React.useState({ x: 0, y: 0 });
    const [copied, setCopied] = React.useState(false);
    const [showChecklist, setShowChecklist] = React.useState(false);
    const [iconAnimatingOut, setIconAnimatingOut] = React.useState(false);
    const [iconAnimatingIn, setIconAnimatingIn] = React.useState(false);

    React.useEffect(() => {
        // Create a resize listener
        const listener = () => {
            const logo = document.getElementById("ai_logo");
            const uie = document.getElementById("user-interface");
            if (logo && uie) {
                // This is not super ideal
                const { right, bottom } = logo.getBoundingClientRect();
                const ui = uie.getBoundingClientRect();
                setPosition({ x: right - ui.x, y: bottom - ui.y });
            }
        };

        listener();
        window.addEventListener('resize', listener);

        return () => {
            window.removeEventListener('resize', listener);
        };
    }, []);

    // Separate effect for curtain fade - triggers every time component mounts
    React.useEffect(() => {
        props.setCurtainFadeSpeed(0);
        props.setCurtainOpacity(1);
        const splashImg = document.querySelector('img[src="assets/ui/splash.png"]') as HTMLImageElement;
        const splashLoaded = splashImg && splashImg.complete && splashImg.naturalHeight !== 0;
        if (!splashImg || splashLoaded) {
            props.setCurtainFadeSpeed(1);
            props.setCurtainOpacity(0);
        } else if (splashImg) {
            splashImg.onload = () => {
                props.setCurtainFadeSpeed(1);
                props.setCurtainOpacity(0);
            };
            splashImg.onerror = () => {
                props.setCurtainFadeSpeed(1);
                props.setCurtainOpacity(0);
            };
        }
    }, []);

    React.useEffect(() => {
        const playText = connected ? getPlayText() : "Join Lobby";
        setButtonText(playText);
    }, [])

    // Clear the connection status when handing over to the game, so the lobby starts from nothing next time.
    React.useEffect(() => clearConnectionStatus, []);

    // Auto-open checklist if there are unannounced achievements
    React.useEffect(() => {
        //Like the checklist icon, not until the tutorial is done. Anything earned before then is announced after
        if (getTrainingLevel() !== TrainingLevel.Trained) return;
        const newAchievements = getUserSettings().unannouncedAchievements;
        if (newAchievements && newAchievements.length > 0) {
            const timer = setTimeout(() => {
                openChecklist();
            }, 400);
            return () => clearTimeout(timer);
        }
    }, []);

    const showSettings = () => {
        props.showSettings(true);
    }

    const showHowToPlay = () => {
        props.showTutorial(allTutorials);
    }

    const hover = () => {
        if (gameClient && connected) {
            gameClient.playUISound(Sound.Menu);
        }
    }

    const setupGame = async (
        trainingLevel: TrainingLevel,
        connectionToServer?: ConnectionToServer,
    ) => {
        if (!gameClient.isConnected && !gameClient.isConnecting) {
            if (!connectionToServer) {
                if (!process.env.is_standalone) {
                    connectionToServer = new WSClient(`ws://${Multiplayer.ServerIP}`);
                }
                else {
                    connectionToServer = WebWorkerClient.getInstance();
                }
            }
            await gameClient.connect(connectionToServer);
        }
        gameClient.setVolume(getMainVolume());
        gameClient.setSFXVolume(getSfxVolume());
        gameClient.setFreqVolume(getSfxVolume());
        gameClient.setMusicVolume(getMusicVolume());
        gameClient.setDontShowTutorial(getDontShowTutorial());
        gameClient.setTrainingLevel(trainingLevel)
        gameClient.playUISound(Sound.ButtonClick);
        gameClient.startMainLoop();

        const result = await gameClient.waitForConnection();
        if (!result) {
            setConnectionError("The game server didn't respond");
            gameClient.endGame();
            return null;
        }
        setConnected(true);
        setButtonText(getPlayText());
        return result;
    };

    const checkSteamAvailable = async (): Promise<boolean> => {
        try {
            return await isSteamNetworkingAvailable();
        } catch (error) {
            console.error("Failed to check Steam availability:", error);
            return false;
        }
    };

    const onGameTreeSelection = async (selection: GameTreeSelection) => {
        switch (selection) {
            case GameTreeSelection.Continue: {
                if (!gameClient.newGame()) return;
                const res = await setupGame(getTrainingLevel());
                if (!res) return;
                if (!res.isReconnecting) { //this wouldn't res
                    startGame(readContinuableSoloSave() ?? GameMode.Solo, Multiplayer.AutosaveName);
                }
                break;
            }
            case GameTreeSelection.Solo: {
                if (!gameClient.newGame()) return;
                const result = await setupGame(getTrainingLevel());
                if (!result) return;
                if (!result.isReconnecting) {
                    startGame(GameMode.Solo);
                }
                break;
            }
            case GameTreeSelection.Ranked: {
                if (!gameClient.newGame()) return;
                onButtonExit();
                const result = await setupGame(getTrainingLevel());
                if (!result) return;
                if (!result.isReconnecting) {
                    startGame(GameMode.Asynchronous);
                }
                break;
            }

            case GameTreeSelection.HostViaSteam:
            case GameTreeSelection.HostViaBrowser: {
                if (!gameClient.newGame()) return;
                onButtonExit();

                if (!await setupGame(TrainingLevel.Trained)) return;

                //Create connection to second player and wait for them to join
                let connectionToSecondPlayer: ConnectionToRemotePeer | null = null;
                if (selection === GameTreeSelection.HostViaSteam) {
                    if (!await checkSteamAvailable()) {
                        setConnectionError("Steam is not available");
                        return;
                    }
                    const onLobbyOpen = (lobbyId: string) => {
                        console.log("Steam lobby is open with id: " + lobbyId);
                        setConnectionStep("Steam invite opened"); //we can't tell whether they actually sent one
                        setConnectionStep("Waiting for other player to join");
                    };

                    const steamPoller = new SteamConnectionPoller();
                    inProgressJoiningConnection.current = steamPoller;
                    const remoteConnectionResult = await steamPoller.openHost(Multiplayer.WaitForConnectionTimeout, onLobbyOpen);
                    if (remoteConnectionResult) {
                        //The peer takes over the poller: it owns the lobby and listen socket for the session
                        connectionToSecondPlayer = new SteamConnectionToRemotePeer(remoteConnectionResult, steamPoller);
                        await connectionToSecondPlayer.host();
                    }
                }
                else {
                    const onLobbyReady = (id: string) => {
                        console.log("Lobby created with id: " + id);
                        gameClient.hostId = id;
                        gameClient.setUIState(); // main loop not running yet; call directly so the link renders before the peer joins
                        setConnectionStep("Invite link created");
                        setConnectionStep("Waiting for other player to join");
                    };

                    const lobbyCode = crypto.randomUUID();

                    inProgressJoiningConnection.current = new WebRTCConnectionPoller();
                    //Same budget as the steam host: this is the "waiting for a friend" window
                    const remoteConnectionResult = await inProgressJoiningConnection.current.openConnection(lobbyCode, onLobbyReady, Multiplayer.WaitForConnectionTimeout);
                    if (remoteConnectionResult) {
                        connectionToSecondPlayer = new WebRTCConnectionToRemotePlayer(lobbyCode, remoteConnectionResult);
                        await connectionToSecondPlayer.host();
                    }
                }
                if (!connectionToSecondPlayer || connectionToSecondPlayer.getState() !== 'connected') {
                    const error = inProgressJoiningConnection.current?.getError();
                    setConnectionError(error);
                    //Both are safe to call: whichever object got as far as owning something releases it
                    connectionToSecondPlayer?.disconnectAndDispose();
                    inProgressJoiningConnection.current?.dispose();
                    inProgressJoiningConnection.current = null;
                    return;
                }
                setConnectionStep("Other player ready");
                if (!gameClient.setRemotePlayerConnection(connectionToSecondPlayer)) {
                    connectionToSecondPlayer.disconnectAndDispose(); //releases the poller and the connection
                    inProgressJoiningConnection.current?.dispose();
                }
                inProgressJoiningConnection.current = null;

                gameClient.updateUI(); //update the lobby id, must be after connect since connect will reset the flag
                break;
            }
            case GameTreeSelection.JoinViaSteam:
            case GameTreeSelection.JoinViaBrowser: {
                if (!gameClient.newGame()) return;
                onButtonExit();

                // A Steam invite arrives as a launch argument or an in-session callback
                // rather than as a lobby link, so it never goes through App's hostId.
                const lobbyToJoin = selection === GameTreeSelection.JoinViaSteam ?
                    getPendingSteamLobbyId() :
                    getQueryParams(window.location.href)['lobby']; //dont use props.hostid
                //the invite is kept until we are actually in, so Retry has something to use
                if (!lobbyToJoin || typeof lobbyToJoin !== 'string') {
                    console.error("GameClient: Invalid lobby id: " + lobbyToJoin)
                    setConnectionError();
                    return;
                }

                setConnectionStep("Connecting");
                let connectionToHost: ConnectionToServer;
                if (selection === GameTreeSelection.JoinViaSteam) {
                    console.log('server type is steam');
                    if (!await checkSteamAvailable()) {
                        setConnectionError("Steam is not available");
                        return;
                    }
                    inProgressJoiningConnection.current = new SteamConnectionPoller();
                    const steamClient = await inProgressJoiningConnection.current.connectToHost(lobbyToJoin);
                    if (!steamClient) {
                        const error = inProgressJoiningConnection.current.getError();
                        setConnectionError(error);
                        inProgressJoiningConnection.current.dispose();
                        inProgressJoiningConnection.current = null;
                        return;
                    }
                    connectionToHost = steamClient;
                }
                else {
                    inProgressJoiningConnection.current = new WebRTCConnectionPoller();
                    let lobby: ConnectionToServer | null = null;
                    try {
                        lobby = await inProgressJoiningConnection.current.joinFromBrowser(lobbyToJoin);
                    }
                    catch (error) {
                        console.error("GameClient: Error joining lobby (rtc): " + lobbyToJoin, error);
                    }
                    if (!lobby) {
                        //TODO: we need to handle this 
                        console.error("GameClient: Failed to join lobby (rtc): " + lobbyToJoin)
                        setConnectionError();
                        inProgressJoiningConnection.current.dispose();
                        inProgressJoiningConnection.current = null;
                        return;
                    }
                    connectionToHost = lobby;
                }
                inProgressJoiningConnection.current = null;

                if (!await setupGame(TrainingLevel.Trained, connectionToHost)) return;
                clearPendingSteamLobbyId(); //consumed: we are in
                setConnectionStep("Waiting for host to start game");
                break;
            }
            case GameTreeSelection.ContinueMultiplayer: {
                const save = readContinuableMultiplayerSave();
                if (!save) {
                    setConnectionError("That saved game is no longer available");
                    return;
                }
                resumeSaveName.current = Multiplayer.MultiplayerAutosaveName;
                break;
            }
            case GameTreeSelection.NewMultiplayerGame: {
                resumeSaveName.current = null;
                break;
            }
            case GameTreeSelection.StartPeerToPeer:
                if (connected) { //should already be connected by host or join
                    setConnectionStep("Game started");
                    startGame(GameMode.LivePeerToPeer, resumeSaveName.current);
                    resumeSaveName.current = null;
                } else {
                    console.warn("Not connected to server, cannot start peer to peer game (button should have been greyed out");
                    //button should have been greyed out
                }
                onButtonExit();
                break;

            case GameTreeSelection.Settings:
                showSettings();
                break;
            case GameTreeSelection.SkipTutorial:
                setTrainingLevel(TrainingLevel.Trained);
                gameClient.setUIState(); //rerender so the buttons gated on training update
                break;
            case GameTreeSelection.Quit:
                gameClient.quit();
                break;
            case GameTreeSelection.LeaveLobby:
                inProgressJoiningConnection.current?.dispose();
                inProgressJoiningConnection.current = null;

                gameClient.endGame();
                clearConnectionStatus(); //so a retry starts from nothing
                break;
        }
    };

    // Reloading the last save after a crash comes through here, and goes the same way as the Continue button
    React.useEffect(() => {
        if (gameClient.continueOnLobby) {
            gameClient.continueOnLobby = false;
            onGameTreeSelection(GameTreeSelection.Continue);
        }
    }, []);

    const copyLink = async () => {
        try {
            const Services = await getWebServicesList();
            let connectText = Services.JoinUrlTemplate.replace('[LOBBY_ID]', props.hostId!);
            if (Debug.UseLocalJoinUrl) {
                connectText = `${window.location.href}?lobby=${props.hostId!}`;
            }
            await navigator.clipboard.writeText(connectText);
            setCopied(!copied);
            console.log('Copying to clipboard was successful');
        } catch (e) {
            console.log('Copying to clipboard was not successful');
        }
    }

    const openChecklist = () => {
        setIconAnimatingOut(true);
        setTimeout(() => {
            setShowChecklist(true);
            //setIconAnimatingOut(false);
        }, UIConstants.ChecklistTransitionSeconds * 1000 * 0.55);
        setTimeout(() => {
            setIconAnimatingOut(false);
        }, UIConstants.ChecklistTransitionSeconds * 1000);
    };

    const closeChecklist = () => {
        clearUnannouncedAchievements();
        setShowChecklist(false);
        setIconAnimatingIn(true);
        setTimeout(() => {
            setIconAnimatingIn(false);
        }, UIConstants.ChecklistTransitionSeconds * 1000);
    };

    const trained = getTrainingLevel() === TrainingLevel.Trained;
    const startPending = gameClient.gameId !== null;
    const mpAllowed = trained; //TODO: Get mp working in demo mode and with continues
    const canContinue = readContinuableSoloSave() !== null;

    return (<>
        <img css={css`
            width: 100%;
            height: auto;
            position: absolute;
            left: 0;
            pointer-events: none;
            top: 0;   
            background-color: #031522
       `} src="assets/ui/splash.png" />
        <StartGameTree
            connected={connected}
            onGameTreeSelection={onGameTreeSelection}
            isSpawnGame={props.isSpawnGame}
            buttonText={buttonText}
            mpAllowed={mpAllowed}
            peerConnected={peerConnected}
            canContinue={canContinue}
            startPending={startPending}
        />
        {
            props.hostId !== undefined && !props.isSpawnGame &&
            <div css={[noticePanelStyle, hostLinkPanelStyle]} onClick={copyLink}>
                <div>
                    <span>Join Id: {props.hostId} </span>
                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                </div>
                <DesktopChromeHostNotice />
            </div>
        }
        {
            props.isSpawnGame && !isDesktopChrome() &&
            <div css={[noticePanelStyle, joinerNoticePanelStyle]}>
                <DesktopChromeJoinerNotice />
            </div>
        }
        {
            trained &&
            <div id="rank" css={css`
                position: absolute;
                top: 85%;
                left: 50%;
                color: ${Colors.UIBackground};
                font-size: 2em;
                transform: translate(-50%, 0);
            `}>
                <div css={css`
                    display: flex;
                    align-items: center;
                `}>
                    <span>Rank:</span>
                    <LeagueMarker rank={getLadderRank()} />
                </div>
                <div css={css`
                    display: flex;
                    align-items: center;
                `}>
                    <span>Wins:</span>
                    <RemainingMarker rank={getLadderRank()} />
                </div>
            </div>
        }
        <div id="lobby_tooltip" css={css`
            display: none;
            position: absolute;
            color: ${Colors.UIDescriptionText};
            font-size: 2em;
            background: ${Colors.UIBackground};
            position: absolute;
            left: 50%;
            top: 10%;
            transform: translate(-50%, -50%);
            padding: 1vw;
            border-radius: 2vh;
            border: inset 0.5vw ${Colors.UITextAndBorders};
        `}></div>
        {(getDismissedEAPopup() === false || getDismissedEAPopup === undefined) &&
            <EAPopup />
        }
        {(trained && !gameClient.isSpawnGame && !showChecklist || iconAnimatingIn || iconAnimatingOut) && (
            <ChecklistIcon
                onClick={openChecklist}
                isAnimatingOut={iconAnimatingOut}
                isAnimatingIn={iconAnimatingIn}
            />
        )}
        {showChecklist && (
            <AchievementChecklist
                dismiss={closeChecklist}
            />
        )}
    </>);
};